import { useMemo } from 'react';
import { Tag } from 'antd';
import { FaUserMd, FaSpa, FaFire } from 'react-icons/fa';

export const useTypeRender = () => {
  return useMemo(() => {
    return (type: string | string[]) => {
      const typeConfigs: {
        [key: string]: {
          bgColor: string;
          textColor: string;
          borderColor: string;
          icon: React.ReactNode;
          text: string;
        };
      } = {
        CLINICAL: {
          bgColor: '#e6f7ff',
          textColor: '#1890ff',
          borderColor: '#91d5ff',
          icon: <FaUserMd />,
          text: 'Clinical',
        },
        AESTHETIC: {
          bgColor: '#f9f0ff',
          textColor: '#722ed1',
          borderColor: '#d3adf7',
          icon: <FaSpa />,
          text: 'Aesthetic',
        },
        LHR: {
          bgColor: '#fff7e6',
          textColor: '#fa8c16',
          borderColor: '#ffd591',
          icon: <FaFire />,
          text: 'LHR',
        },
      };

      // Handle both single string, array of strings, and comma-separated string
      let types: string[];
      if (Array.isArray(type)) {
        types = type;
      } else if (typeof type === 'string' && type.includes(',')) {
        // Handle comma-separated string from backend
        types = type
          .split(',')
          .map(t => t.trim())
          .filter(Boolean);
      } else {
        types = [type];
      }

      // If no types or empty array, return default
      if (!types.length || (types.length === 1 && !types[0])) {
        return (
          <Tag
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              fontWeight: 500,
              padding: '4px 8px',
              borderRadius: '6px',
              lineHeight: '1.2',
              backgroundColor: '#f5f5f5',
              color: '#8c8c8c',
              border: '1px solid #d9d9d9',
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center' }}>
              <FaUserMd />
            </span>
            <span>-</span>
          </Tag>
        );
      }

      // If single type, render as before
      if (types.length === 1) {
        const config = typeConfigs[types[0]] || {
          bgColor: '#f5f5f5',
          textColor: '#8c8c8c',
          borderColor: '#d9d9d9',
          icon: <FaUserMd />,
          text: types[0],
        };

        return (
          <Tag
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              fontWeight: 500,
              padding: '4px 8px',
              borderRadius: '6px',
              lineHeight: '1.2',
              backgroundColor: config.bgColor,
              color: config.textColor,
              border: `1px solid ${config.borderColor}`,
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center' }}>
              {config.icon}
            </span>
            <span>{config.text}</span>
          </Tag>
        );
      }

      // If multiple types, render multiple tags
      return (
        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
          {types.map((typeValue, index) => {
            const config = typeConfigs[typeValue] || {
              bgColor: '#f5f5f5',
              textColor: '#8c8c8c',
              borderColor: '#d9d9d9',
              icon: <FaUserMd />,
              text: typeValue,
            };

            return (
              <Tag
                key={index}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                  fontWeight: 500,
                  padding: '2px 6px',
                  borderRadius: '4px',
                  lineHeight: '1.2',
                  fontSize: '11px',
                  backgroundColor: config.bgColor,
                  color: config.textColor,
                  border: `1px solid ${config.borderColor}`,
                }}
              >
                <span
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    fontSize: '10px',
                  }}
                >
                  {config.icon}
                </span>
                <span>{config.text}</span>
              </Tag>
            );
          })}
        </div>
      );
    };
  }, []);
};
